import { createContext, useContext, useMemo, useState } from "react";
import { loadState, saveState } from "../lib/storage";
import { useDebounce } from "../lib/useDebounce";
import { StreakProvider, useStreakContext } from "./StreakContext";
import { PrayerProvider, usePrayerContext } from "./PrayerContext";
import { DeedsProvider, useDeeds } from "./DeedsContext";
import { SettingsProvider, useSettings } from "./SettingsContext";

/**
 * AppStateContext - High-level context wrapper
 * 
 * This context now delegates to 4 specialized contexts:
 * 1. StreakContext - Streak state and actions
 * 2. PrayerContext - Prayer logging and notes
 * 3. DeedsContext - Daily deed tracking
 * 4. SettingsContext - App settings and privacy
 * 
 * Benefits:
 * - Prevents cascade re-renders (changing prayer doesn't re-render settings)
 * - Each context is independently memoized
 * - Debounced storage persistence prevents main-thread blocking
 */
const AppStateContext = createContext(null);


function AppStateProviderInner({ children, initialState }) {
  const stripeState = useStreakContext();
  const prayerState = usePrayerContext();
  const deedsState = useDeeds();
  const settingsState = useSettings();

  const [storageSynced, setStorageSynced] = useState(false);

  // Combine all states for unified access if needed
  const combinedState = useMemo(
    () => ({
      ...stripeState.state,
      ...prayerState.state,
      ...deedsState.state,
      ...settingsState.state,
    }),
    [
      stripeState.state,
      prayerState.state,
      deedsState.state,
      settingsState.state,
    ]
  );

  // Debounce storage persistence (wait 1 second of inactivity)
  useDebounce(
    () => {
      try {
        saveState(combinedState);
        setStorageSynced(true);
      } catch (error) {
        console.error("❌ Error saving state to storage:", error);
        // Don't crash the app, just log error
      }
    },
    1000, // 1 second debounce
    [combinedState]
  );

  // Apply dark mode to document
  useMemo(() => {
    if (settingsState.state.settings.darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [settingsState.state.settings.darkMode]);

  // Get today's date in LOCAL timezone (not UTC)
  // This fixes the issue where users in UTC-x timezones would see yesterday's date
  const today = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const date = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${date}`;
  }, []);

  // Memoize unified context value
  const value = useMemo(
    () => ({
      state: combinedState,
      actions: {
        ...stripeState.actions,
        ...prayerState.actions,
        ...deedsState.actions,
        ...settingsState.actions,
      },
      today,
      masked: settingsState.masked,
      privacyLocked: settingsState.privacyLocked,
      dailyDeed: deedsState.dailyDeed,
      deedCompleted: deedsState.deedCompleted,
      currentDurationMs: stripeState.currentDurationMs,
      lastDurationMs: stripeState.lastDurationMs,
      bestDurationMs: stripeState.bestDurationMs,
      storageSynced,
    }),
    [
      combinedState,
      stripeState.actions,
      stripeState.currentDurationMs,
      stripeState.lastDurationMs,
      stripeState.bestDurationMs,
      prayerState.actions,
      deedsState.actions,
      deedsState.dailyDeed,
      deedsState.deedCompleted,
      settingsState.actions,
      settingsState.masked,
      settingsState.privacyLocked,
      today,
      storageSynced,
    ]
  );

  return (
    <AppStateContext.Provider value={value}>
      {children}
    </AppStateContext.Provider>
  );
}

export function AppStateProvider({ children }) {
  const initialState = loadState();
  
  // If state is corrupted/null and we can't load, ensure we have something
  // Each provider will validate their portion independently
  const safeInitialState = initialState || {};

  return (
    <StreakProvider initialState={safeInitialState}>
      <PrayerProvider initialState={safeInitialState}>
        <DeedsProvider initialState={safeInitialState}>
          <SettingsProvider initialState={safeInitialState}>
            <AppStateProviderInner initialState={safeInitialState}>
              {children}
            </AppStateProviderInner>
          </SettingsProvider>
        </DeedsProvider>
      </PrayerProvider>
    </StreakProvider>
  );
}


export function useAppState() {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error("useAppState must be used within AppStateProvider");
  }
  return context;
}

// Direct context accessors for fine-grained control
export { useStreakContext } from "./StreakContext";
export { usePrayerContext } from "./PrayerContext";
export { useDeeds } from "./DeedsContext";
export { useSettings } from "./SettingsContext";
